import type { HubSelectOption } from 'src/components/cmui/hub/hub-select';

import { PERSONA_TOKEN_OPTIONS } from 'src/utils/moments/view/moment-filter-bar-labels';

import { hubSelectGroup, type HubFilterGroup } from 'src/components/cmui/hub/hub-filter-groups';

/**
 * 브랜드 허브의 **필터 축 다섯 벌**(제품군 · 소분류 · 채널 · 국가 · 페르소나) — 목록(`BrandDirectory`)과 상세 머리(`BrandHubConditionBar`)가 같이 쓴다.
 *
 * 🔑 목록과 상세가 **같은 상태**를 보므로 축의 모양도 하나다 — 제품군은 줄에 꺼내 두고(`pinned`) 나머지는 「필터」 판에 모인다.
 * 🔑 `pinMain` 이 거짓이면 제품군도 「필터」 판의 첫 섹션으로 들어가 「필터 · N」의 N 에 세인다(목록의 폰 폭).
 * ⚠️ 대분류를 바꾸면 소분류는 «푼다» — 남겨 두면 다른 대분류의 소분류가 걸린다.
 * 🔑 페르소나 선택지는 사이드바 페르소나 칩과 같은 표기다(`PERSONA_TOKEN_OPTIONS` — 일반 · 광고/협찬 · 브랜드 계정).
 * @param def 값·선택지·바꾸는 함수는 화면의 것 · `keepEmptySub` 면 소분류가 없어도 칸을 남긴다(흐리게) · `pinMain` 기본은 참
 */
export function brandHubFilterGroups(def: {
  mainCode: string;
  mainOptions: HubSelectOption[];
  mainDisabled?: boolean;
  subCode: string;
  subOptions: HubSelectOption[];
  keepEmptySub?: boolean;
  /** 제품군을 줄에 꺼내 둘지 — 거짓이면 「필터」 판 안에 선다 */
  pinMain?: boolean;
  onPickCategory: (mainCode: string, subCode: string) => void;
  channels: string[];
  channelOptions: HubSelectOption[];
  onToggleChannel: (channel: string) => void;
  countries: string[];
  countryOptions: HubSelectOption[];
  onToggleCountry: (code: string) => void;
  /** 고른 페르소나 토큰(`organic` · `sponsored` · `brandAccount`) — 비면 「전체」 */
  personas: string[];
  onTogglePersona: (token: string) => void;
}): HubFilterGroup[] {
  const { mainCode, subCode, channels, countries, personas, onPickCategory, onToggleChannel, onToggleCountry, onTogglePersona } = def;
  return [
    hubSelectGroup({
      key: 'main',
      label: '제품군',
      pinned: def.pinMain ?? true,
      value: mainCode,
      empty: '전체',
      disabled: def.mainDisabled,
      options: def.mainOptions,
      onChange: (next) => onPickCategory(next, ''),
      onClear: () => onPickCategory('', ''),
    }),
    ...(def.subOptions.length > 0 || def.keepEmptySub ? [hubSelectGroup({
      key: 'sub',
      label: '소분류',
      value: subCode,
      empty: '전체',
      disabled: def.subOptions.length === 0,
      options: def.subOptions,
      onChange: (next) => onPickCategory(mainCode, next),
      onClear: () => onPickCategory(mainCode, ''),
    })] : []),
    hubSelectGroup({
      key: 'channel',
      label: '채널',
      multiple: true,
      value: channels,
      empty: '전체',
      options: def.channelOptions,
      onChange: onToggleChannel,
      onClear: () => channels.forEach(onToggleChannel),
    }),
    hubSelectGroup({
      key: 'country',
      label: '국가',
      multiple: true,
      value: countries,
      empty: '전체',
      options: def.countryOptions,
      onChange: onToggleCountry,
      onClear: () => countries.forEach(onToggleCountry),
    }),
    hubSelectGroup({
      key: 'persona',
      label: '페르소나',
      multiple: true,
      value: personas,
      empty: '전체',
      options: PERSONA_TOKEN_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
      onChange: onTogglePersona,
      onClear: () => personas.forEach(onTogglePersona),
    }),
  ];
}
